import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Panel } from "@/lovable-demo/components/bits";
import { Input } from "@/components/ui/input";
import { ExpandableClaimRow } from "@/lovable-demo/components/record-extensions";
import { fmtCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { ClaimsPrediction } from "@/lovable-demo/components/claims-prediction";

export const Route = createFileRoute("/demo/claims")({
  head: () => ({
    meta: [
      { title: "Warranty Claims — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Searchable warranty claim database with VIN, failure and repair dates, mileage, dealer, cost and the governed problem record each claim rolls up to.",
      },
      { property: "og:title", content: "Warranty Claims — Broadview Warranty Intelligence" },
      {
        property: "og:description",
        content:
          "Claim-level warranty data by VIN, date and description, linked to governed problem records.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ClaimsPage,
});

function ClaimsPage() {
  const { claims, data } = useWarranty();
  const [query, setQuery] = useState("");
  const [recordId, setRecordId] = useState("all");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return claims
      .filter((c) => (recordId === "all" ? true : c.recordId === recordId))
      .filter((c) => (status === "all" ? true : c.status === status))
      .filter((c) =>
        q
          ? c.vin.toLowerCase().includes(q) ||
            c.claimNumber.toLowerCase().includes(q) ||
            c.description.toLowerCase().includes(q) ||
            c.dealer.toLowerCase().includes(q)
          : true,
      )
      .sort((a, b) => b.repairDate.localeCompare(a.repairDate));
  }, [claims, query, recordId, status]);

  const totalCost = filtered.reduce((sum, c) => sum + c.totalCost, 0);
  const codeFor = (id: string) => data.records.find((r) => r.id === id)?.code ?? "—";

  return (
    <AppShell
      title="Warranty Claims"
      subtitle="Claim-level records by VIN, date and description, linked to governed problem records"
    >
      <ClaimsPrediction
        claims={claims}
        records={data.records.filter((r) => recordId === "all" || r.id === recordId)}
      />
      <Panel
        title={`${fmtNumber(filtered.length)} claims · ${fmtCurrency(totalCost)} claimed cost`}
        subtitle="Search by VIN, claim number, dealer or description"
      >
        <div className="mb-3 grid gap-2 sm:grid-cols-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search VIN, claim number, dealer…"
            className="bg-elevated text-sm"
          />
          <select
            value={recordId}
            onChange={(e) => setRecordId(e.target.value)}
            className="rounded border border-input bg-elevated px-2 py-2 text-sm"
          >
            <option value="all">All problem records</option>
            {data.records.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} · {r.title}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-input bg-elevated px-2 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="approved">Approved</option>
            <option value="in-review">In review</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-xs">
            <thead className="text-[0.66rem] uppercase tracking-[0.14em] text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3">Claim</th>
                <th className="py-2 pr-3">VIN</th>
                <th className="py-2 pr-3">Failure</th>
                <th className="py-2 pr-3">Repair</th>
                <th className="py-2 pr-3">Mileage</th>
                <th className="py-2 pr-3">Description</th>
                <th className="py-2 pr-3">Dealer</th>
                <th className="py-2 pr-3">Cost</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Record</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <ExpandableClaimRow key={c.id} claim={c} colSpan={10}>
                  <td className="num py-2 pr-3 font-semibold">{c.claimNumber}</td>
                  <td className="num py-2 pr-3">{c.vin}</td>
                  <td className="num py-2 pr-3">{c.failureDate}</td>
                  <td className="num py-2 pr-3">{c.repairDate}</td>
                  <td className="num py-2 pr-3">{fmtNumber(c.mileage)}</td>
                  <td className="max-w-[24rem] py-2 pr-3 text-muted-foreground">{c.description}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.dealer}</td>
                  <td className="num py-2 pr-3">{fmtCurrency(c.totalCost)}</td>
                  <td className="py-2 pr-3 capitalize text-muted-foreground">{c.status}</td>
                  <td className="py-2">
                    <Link
                      to="/demo/problem-record/$id"
                      params={{ id: c.recordId }}
                      className="num text-primary hover:underline"
                    >
                      {codeFor(c.recordId)}
                    </Link>
                  </td>
                </ExpandableClaimRow>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-6 text-center text-muted-foreground">
                    No claims match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </AppShell>
  );
}
